import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';

import { getFormikId } from '../helpers';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { Card } from 'core/components/card';
import { tooltipHoverTexts, lockedValuesTooltipTexts } from 'core/helpers/constants';
import { Tooltip } from 'core/components/tooltip';
import infoIconWhite from 'core/assets/svg/ic-info-white.svg';
import lockIconOrange from 'core/assets/svg/ic-locked.svg';
import { FormField } from 'core/components/form';
import { useToast } from 'core/components/toast';

import useStyles from './pip-coverages.styles';

const PipCoveragesInfoCard = ({ opt, residentsList }) => {
  const classes = useStyles();
  const { values, setFieldValue, initialValues } = useFormikContext();
  const toast = useToast();
  const { inOfferEditRoute, inPolicyRoute } = useRouteHelper();

  useEffect(() => {
    if (opt.id !== initialValues.autoCoverage.policyLimitPIPME) {
      setFieldValue('auto.pipEligibilityConfirmation', false);
    }
  }, [opt.id]);

  useEffect(() => {
    if (residentsList?.length > (initialValues?.nonDrivers || [])?.concat(initialValues?.drivers)?.length) {
      setFieldValue('auto.pipEligibilityConfirmation', false);
      toast.notify({
        type: 'success',
        message:
          'The list of household residents has been updated. Please verify that the policy is still eligible for the selected PIP coverage.'
      });
    }
  }, [residentsList?.length]);

  const allResidentsOptOut = residentsList.every((resident) => resident.pipHasQHC);

  return (
    <Grid className={classes.warningContainer}>
      {inOfferEditRoute ? (
        <Card className={classes.infoCard}>
          <img src={infoIconWhite} className={classes.iconSmall} alt="Notification Icon" />
          <Grid className={classes.notificationContentWithTitle}>
            <p className={classes.titleText}>Eligibility restrictions apply</p>
            {tooltipHoverTexts.policyLimitPIPME(opt.id)}
            <p>
              After purchase, we’ll reach out for proof. If the proof isn’t provided, we’ll update your PIP coverage to
              unlimited, which will increase your premium.
            </p>
          </Grid>
        </Card>
      ) : (
        <p>{tooltipHoverTexts.policyLimitPIPME(opt.id)}</p>
      )}
      {opt.id === '250000W/EXCLUDED' && (
        <Grid>
          <p>
            {inOfferEditRoute
              ? 'Which of these household residents have QHC and would like to waive PIP coverage?'
              : 'Household residents with QHC waiving PIP coverage:'}
          </p>
          {residentsList.map((person) => (
            <Grid key={person.id} className={classes.rowContainer}>
              <FormField
                key={person.id}
                id={`${getFormikId(person, values)}.pipHasQHC`}
                name={`${getFormikId(person, values)}.pipHasQHC`}
                type="checkbox"
                label={`${person.firstName} ${person.lastName}`}
                initialValue={false}
                disabled={person.isPrimary}
              />
              {person.isPrimary && (
                <Tooltip
                  text={lockedValuesTooltipTexts.isPrimaryAndWaivedPip}
                  className={classes.primaryLocked}
                  placement="right"
                  iconSize="icon16"
                  smallIcon={{ src: lockIconOrange, alt: 'Lock icon' }}
                />
              )}
            </Grid>
          ))}
          {allResidentsOptOut && (
            <Card className={classes.infoCard}>
              <img src={infoIconWhite} className={classes.iconAllOptOut} alt="Notification Icon" />
              <Grid className={classes.notificationContentWithTitle}>
                <p>
                  Even if all household residents are eligible to waive/exclude PIP coverage, Michigan still requires
                  auto policies to carry a minimum of $250,000 in coverage.
                </p>
              </Grid>
            </Card>
          )}

          {inOfferEditRoute && <p>Please confirm the following:</p>}
        </Grid>
      )}
      {inPolicyRoute && (
        <Card className={classes.infoCard}>
          <img src={infoIconWhite} className={classes.iconAllOptOut} alt="Notification Icon" />
          <Grid className={classes.notificationContentWithTitle}>
            <p>
              Changes to drivers, household residents, or PIP ME selections will require re-verification of eligibility
            </p>
          </Grid>
        </Card>
      )}
      {inOfferEditRoute && (
        <FormField
          id={'auto.pipEligibilityConfirmation'}
          name={'auto.pipEligibilityConfirmation'}
          type="checkbox"
          label='I acknowledge that PIP Medical will be updated to "Unlimited" if proof of eligibility for the selected option is not provided after purchase.'
          mode="dark"
          xs={12}
          className={classes.checkbox}
        />
      )}
    </Grid>
  );
};

PipCoveragesInfoCard.propTypes = {};

export default PipCoveragesInfoCard;
